import React, { useState, useEffect } from "react";
import QrScanner from "qr-scanner";
import axios from "axios";
import { FaArrowRight } from "react-icons/fa";
import moment from "moment/moment";
import BASE_URL from "../Config";

export default function QRTimeBasedButtons() {
  const [eventData, setEventData] = useState([]);
  const [scannedLink, setScannedLink] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [batchTimeData, setBatchTimeData] = useState(null);
  const [userActions, setUserActions] = useState([]);
  const [activityData, setActivityData] = useState(null);
  const [batchType, setBatchType] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [scanned, setScanned] = useState(false);
  const [currentDay, setCurrentDay] = useState(null);
  const videoRef = React.useRef(null);
  const scannerRef = React.useRef(null);
  const [mealMap, setMealMap] = useState({});

  useEffect(() => {
    const getPaymentEventData = async () => {
      try {
        const res = await axios.post(`${BASE_URL}/getMasterEvent`);
        setEventData(res.data?.data || []);
      } catch (e) {
        console.error(e);
      }
    };
    getPaymentEventData();
  }, [showScanner]);

  const getActivityTypeData = async (userId) => {
    try {
      const res = await axios.post(`${BASE_URL}/getActivityType`, {
        id: userId,
      });
      const activities = res.data?.data?.activity || [];
      const actions = res.data?.data?.userActions || [];
      setBatchTimeData(activities);
      setUserActions(actions);
      // build name→ID map
      const map = {};
      activities.forEach((a) => (map[a.Activity_Name] = a.Activity_Type_ID));
      setMealMap(map);
      return activities;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const getMealName = (id) =>
    Object.entries(mealMap).find(([name, _id]) => _id === id)?.[0] || "Unknown";

  // ⏰ Utility: parse 12.3 → 12:03, 12.30 → 12:30
  const parseTime = (timeVal) => {
    if (timeVal == null) return 0;

    const timeStr = String(timeVal); // ensure it's a string
    const parts = timeStr.split(".");
    const hours = parseInt(parts[0], 10) || 0;
    let minutes = 0;

    if (parts[1]) {
      // "3" → "03", "30" → "30"
      const minStr = parts[1].padEnd(2, "0").slice(0, 2);
      minutes = parseInt(minStr, 10) || 0;
    }

    return hours + minutes / 60; // decimal hours
  };

  const getActionOptions = async () => {
    if (!Array.isArray(batchTimeData)) return [];

    const now = new Date();
    const t = now.getHours() + now.getMinutes() / 60; // current time in decimal

    return batchTimeData.reduce((acc, a) => {
      const from = parseTime(a.From_Time);
      const to = parseTime(a.To_Time);

      if (t >= from && t < to) {
        acc.push(a.Activity_Type_ID);
      }
      return acc;
    }, []);
  };

  const insertActionActivity = async (uId, actionType) => {
    // ✅ Check if action_type 31 exists in the array
    const hasCheckIn = userActions.some((action) => action.action_type === 31);

    // ❌ If not found, show a message and return early
    if (!hasCheckIn && actionType !== 31) {
      setPopupData({
        userId: uId,
        action_type: actionType,
        message: "Please check in first",
        dateTime: new Date().toLocaleString(),
        isError: true,
      });
      return;
    }

    // ✅ Proceed with activity insert
    const payload = {
      userId: uId,
      action_type: actionType,
      date: new Date().toISOString(),
      Add_By_User: 1,
      Update_By_User: 1,
    };

    setActivityData(payload);

    try {
      const res = await axios.post(`${BASE_URL}/insertActionActivity`, payload);
      setPopupData({
        ...payload,
        message: res.data.message || "Action recorded",
        dateTime: new Date().toLocaleString(),
      });
    } catch (e) {
      const msg = e.response?.data?.message || "Error";
      setPopupData({
        ...payload,
        message: msg,
        dateTime: new Date().toLocaleString(),
        isError: true,
      });
    }
  };

  const getBadgeTypeCondition = async (uId) => {
    try {
      const res = await axios.post(`${BASE_URL}/getBadgeType`, { id: uId });
      setBatchType(res.data?.data?.batch_assign);
    } catch (e) {
      console.error(e);
    }
  };

  const getSingleUserInfoCall = async (uId) => {
    try {
      const res = await axios.post(`${BASE_URL}/getSingleUserInfo`, {
        id: uId,
      });
      setUserData(res.data?.data);
    } catch (e) {
      console.error(e);
    }
  };

  const startScanner = async () => {
    setShowScanner(true);
    await new Promise((r) => setTimeout(r, 100));
    if (!videoRef.current) return;
    videoRef.current.disablePictureInPicture = true;
    videoRef.current.setAttribute("playsinline", true);
    scannerRef.current?.destroy();

    scannerRef.current = new QrScanner(
      videoRef.current,
      async (result) => {
        const scannedURL = result.data;
        setScannedLink(scannedURL);
        scannerRef.current.stop();
        setShowScanner(false);

        const [, , , , userId, extraParam] = scannedURL.split("/");

        await getActivityTypeData(userId);
        await getSingleUserInfoCall(userId);
        await getBadgeTypeCondition(userId);

        // special badge reissue
        try {
          const br = await axios.post(`${BASE_URL}/getBadgeType`, {
            id: userId,
          });
          if (br.data?.data?.batch_assign === "3" && extraParam === "0") {
            const upd = moment(userData?.updated_at).format("DD/MM/YYYY");
            setPopupData({
              userId,
              dateTime: new Date().toLocaleString(),
              message: `Card ReIssued . ${upd}.`,
              isError: true,
            });
            return;
          }
        } catch {
          setPopupData({
            userId,
            dateTime: new Date().toLocaleString(),
            message: "Error verifying card.",
          });
          return;
        }

        // determine currentDay
        const today = moment().format("YYYY-MM-DD");
        const sd = moment(eventData[0]?.start_date).format("YYYY-MM-DD");
        const ed = moment(eventData[0]?.end_date).format("YYYY-MM-DD");

        if (today === sd) setCurrentDay("first");
        else if (today === ed) setCurrentDay("second");
        else {
          setPopupData({
            userId,
            dateTime: new Date().toLocaleString(),
            message: "Invalid date for activity.",
            isError: true,
          });
          return;
        }

        setScanned(true);
      },
      {
        returnDetailedScanResult: true,
        highlightScanRegion: true,
        highlightCodeOutline: true,
      }
    );

    scannerRef.current.start();
  };

  useEffect(() => {
    const fetchFilteredOptions = async () => {
      if (!batchTimeData?.length) {
        console.warn("batchTimeData not ready yet.");
        return;
      }

      const now = moment();
      const nowTime = now.hours() + now.minutes() / 60;

      // Get Cocktail & Dinner data from batchTimeData (by action_type)
      const cocktailData = batchTimeData.find((a) => a.Activity_Type_ID === 2);
      const dinnerData = batchTimeData.find((a) => a.Activity_Type_ID === 3);

      // Check if current time is within Cocktail or Dinner slot
      const isCocktailTime =
        cocktailData &&
        nowTime >= parseTime(cocktailData.From_Time) &&
        nowTime < parseTime(cocktailData.To_Time);

      const isDinnerTime =
        dinnerData &&
        nowTime >= parseTime(dinnerData.From_Time) &&
        nowTime < parseTime(dinnerData.To_Time);

      const isEvening = isCocktailTime || isDinnerTime;

      const cocktailId = mealMap["Cocktail"]; // action_type 2
      const dinnerId = mealMap["Dinner"]; // action_type 3
      const [, , , , uId] = scannedLink?.split("/") || [];

      // Refetch userActions after Cocktail insert
      const refreshUserActions = async () => {
        const updated = await getActivityTypeData(uId); // This updates userActions state
        return updated;
      };

      // === EVENING FLOW ===
      if (isEvening) {
        const hasCocktail = userActions?.some((u) => u.action_type === 3); // Cocktail
        const hasDinner = userActions?.some((u) => u.action_type === 2); // Dinner

        // 1. Cocktail not done
        if (!hasCocktail && cocktailId && isCocktailTime) {
          setFilteredOptions([cocktailId]);
          setPopupData({
            userId: uId,
            options: [cocktailId],
            dateTime: new Date().toLocaleString(),
            message: "Select activity:",
          });
          return;
        }

        // 2. Cocktail done, but Dinner not done
        if (hasCocktail && !hasDinner && dinnerId && isDinnerTime) {
          setFilteredOptions([dinnerId]);
          setPopupData({
            userId: uId,
            options: [dinnerId],
            dateTime: new Date().toLocaleString(),
            message: "Select one activity:",
          });
          return;
        }

        // 3. Both done or outside time
        setPopupData({
          userId: uId,
          dateTime: new Date().toLocaleString(),
          message:
            "Cocktail and Dinner already completed or not in time window.",
          isError: true,
        });
        return;
      }

      // === DAYTIME LOGIC ===
      let opts = await getActionOptions();
      opts = opts.filter((act) => {
        if (currentDay === "first") {
          return act >= 1 && act <= 10;
        } else {
          return act >= 11 && act <= 20;
        }
      });

      const hasDinner = userActions?.some((u) => u.action_type === 3);
      if (dinnerId && hasDinner) {
        opts = opts.filter((id) => id !== dinnerId);
      }

      setFilteredOptions(opts);

      if (opts.length === 1) {
        await insertActionActivity(uId, opts[0]);
        await refreshUserActions(); // Refetch after insertion
      } else if (opts.length === 2) {
        setPopupData({
          userId: uId,
          options: opts,
          dateTime: new Date().toLocaleString(),
          message: "Select activity:",
        });
      } else {
        setPopupData({
          userId: uId,
          dateTime: new Date().toLocaleString(),
          message: "No valid activity for today.",
          isError: true,
        });
      }
    };

    if (scanned && currentDay) {
      fetchFilteredOptions();
    }
  }, [scanned, currentDay]);

  // stop scanner
  const stopScanner = () => {
    scannerRef.current?.stop();
    setShowScanner(false);
  };

  const closePopup = () => {
    setPopupData(null);
    setScannedLink(null);
    setScanned(false);
  };

  const handleMealSelection = (actionType) => {
    const [, , , , uId] = scannedLink?.split("/") || [];
    insertActionActivity(uId, actionType);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-100 flex flex-col items-center justify-center p-6">
      <div className="bg-white shadow-2xl rounded-3xl p-10 w-full max-w-2xl border border-gray-200 transition-all duration-300 hover:shadow-indigo-200">
        <h1 className="text-4xl font-extrabold text-center mb-10 text-indigo-800 tracking-wide">
          Activity
        </h1>

        {!scannedLink && !showScanner && (
          <div className="flex justify-center">
            <button
              onClick={startScanner}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 rounded-full text-lg shadow-md transition-transform hover:scale-105"
            >
              Scan Card <FaArrowRight />
            </button>
          </div>
        )}

        {/* <div className="flex justify-center mt-4">
          <button className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-full text-lg shadow-md transition-transform hover:scale-105">
            Instant Entry <FaArrowRight />
          </button>
        </div> */}
      </div>

      {showScanner && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center">
          <video
            ref={videoRef}
            className="w-80 h-80 object-cover rounded-lg shadow-xl border-4 border-white"
            autoPlay
            muted
          />

          <div className="mt-4 text-white text-lg font-semibold animate-pulse">
            Scanning for QR...
          </div>

          <button
            onClick={stopScanner}
            className="absolute top-6 right-6 bg-red-600 hover:bg-red-700 transition text-white px-4 py-2 rounded-full shadow-lg"
          >
            ✕
          </button>
        </div>
      )}

      {popupData && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md text-center border-t-4 border-indigo-600">
            {popupData.isError ? (
              <>
                <div className="text-red-600 text-4xl mb-4">❌</div>
                <h2 className="text-2xl font-bold text-red-700 mb-4">
                  Action Failed
                </h2>
                <p className="text-gray-600 mb-2">
                  <strong>Name:</strong> {userData?.name}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Action Type:</strong>{" "}
                  {activityData?.action_type > 0
                    ? getMealName(activityData.action_type)
                    : "Invalid Time"}
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Date & Time:</strong>{" "}
                  {moment(popupData.dateTime).format("DD/MM/YYYY hh:mm A")}
                </p>
                <p className="text-red-600 font-semibold mb-6">
                  {popupData.message || "An unexpected error occurred."}
                </p>
                <button
                  onClick={closePopup}
                  className="px-6 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow"
                >
                  Close
                </button>
              </>
            ) : popupData.options ? (
              <>
                <h2 className="text-2xl font-bold text-indigo-700 mb-4">
                  User Details ✅
                </h2>
                <p className="text-gray-600 mb-2">
                  <strong>Name:</strong> {userData?.name}
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Date & Time:</strong>{" "}
                  {moment(popupData.dateTime).format("DD/MM/YYYY hh:mm A")}
                </p>
                <p className="text-gray-700 font-medium mb-6">
                  {popupData.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popupData.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => handleMealSelection(opt)}
                      className="px-5 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 shadow-md"
                    >
                      {getMealName(opt)}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-indigo-700 mb-4">
                  User Details ✅
                </h2>
                <p className="text-gray-600 mb-2">
                  <strong>Name:</strong> {userData?.name}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Action Type:</strong>{" "}
                  {activityData?.action_type > 0
                    ? getMealName(activityData.action_type)
                    : "Invalid Time"}
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Date & Time:</strong>{" "}
                  {moment(popupData.dateTime).format("DD/MM/YYYY hh:mm A")}
                </p>
                <p className="text-green-600 font-semibold mb-6">
                  {popupData.message}
                </p>
                <button
                  onClick={closePopup}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 shadow"
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
